import { centsToFormattedDollars } from '@fruitful/fruitils';
import React, {
    type Dispatch,
    type SetStateAction,
    useEffect,
    useState,
} from 'react';
import { type LayoutChangeEvent, Pressable, View } from 'react-native';
import Animated, {
    type SharedValue,
    useAnimatedProps,
    useAnimatedStyle,
    useSharedValue,
    withSpring,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Path, Rect, Text as SvgText } from 'react-native-svg';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    FruitfulLogo,
    getDefaultFontFamily,
    Icon,
    redactPrivate,
    SquarePenIcon,
    TriangleAlertIcon,
    useThemeColors,
} from 'smoothie';

import { MoneyMapFilterState } from '../types/MoneyMapFilterState';
import { type MoneyMapRedesignMode } from '../types/MoneyMapRedesignMode';

export type MoneyMapVariant = 'default' | 'small';

const useMoneyMapColors = () => {
    const { getColor } = useThemeColors();

    return {
        income: {
            fill: getColor('$lemon'),
            light: getColor('$lemonLight'),
            lightest: getColor('$lemonLightest'),
            darkest: getColor('$lemonDarkest'),
            text: getColor('$lemonDarkest'),
        },
        bills: {
            fill: getColor('$water'),
            light: getColor('$waterLight'),
            lightest: getColor('$waterLightest'),
            dark: getColor('$waterDark'),
            text: getColor('$waterDarkest'),
        },
        spend: {
            fill: getColor('$leaf'),
            light: getColor('$leafLight'),
            lightest: getColor('$leafLightest'),
            dark: getColor('$leafDark'),
            text: getColor('$leafDarkest'),
        },
        goals: {
            fill: getColor('$petal'),
            light: getColor('$petalLight'),
            lightest: getColor('$petalLightest'),
            dark: getColor('$petalDark'),
            text: getColor('$petalDarkest'),
        },
        logoBg: getColor('$leafDark'),
    };
};

const MAX_DEFAULT_WIDTH = 402;
const MAX_SMALL_WIDTH = 290;
const SELECTION_TRANSITION_MS = 250;
const CURVE_HANDLE_PERCENT = 0.64;
const REGULAR_FONT_FAMILY = getDefaultFontFamily();
const REGULAR_FONT_WEIGHT = '500';

const DEFAULT_LAYOUT = {
    height: 520,
    pillTop: 12,
    pillWidth: 140,
    pillHeight: 64,
    connectorStartY: 88,
    connectorEndY: 104,
    logoTop: 116,
    logoSize: 48,
    curveStartY: 176,
    blocksBottom: 508,
    maxBarHeight: 240,
    minBarHeight: 64,
    barGap: 16,
    horizontalPadding: 2,
    curveToBarGap: 12,
    strokeWidth: 12,
} as const;

const SMALL_LAYOUT = {
    height: 360,
    pillWidth: 140,
    pillHeight: 64,
    curveStartY: 72,
    blocksBottom: 304,
    maxBarHeight: 120,
    minBarHeight: 8,
    barGap: 24,
    curveToBarGap: 8,
    strokeWidth: 8,
    labelsTop: 316,
} as const;

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AnimatedRect = Animated.createAnimatedComponent(Rect);
const AnimatedText = Animated.createAnimatedComponent(SvgText);

export interface MoneyMapVisualProps {
    incomeTotalCents: number;
    billsTotalCents?: number | null;
    spendTotalCents?: number | null;
    goalsTotalCents?: number | null;
    /** New-design layout variant. Defaults to 'default'. */
    variant?: MoneyMapVariant;
    /** When provided, enables tap-to-filter interaction. */
    filter?: MoneyMapFilterState;
    /** Required when filter is provided. */
    setFilter?: Dispatch<SetStateAction<MoneyMapFilterState>>;
    /** Whether the member is eligible for income splitting (shows the Fruitful split node). */
    isIncomeSplitEligible?: boolean;
    /** Whether to animate bars on mount and data changes. Defaults to true. */
    animated?: boolean;
    /** Overrides for bar height constraints. */
    visualSizing?: {
        maxExpandedBarHeight?: number;
        maxCollapsedBarHeight?: number;
    };
    /** Redesign mode controlling visual state. Defaults to 'edit-mode'. */
    mode?: MoneyMapRedesignMode;
    /** Called when go-your-own-way local edits change validity (e.g. income too low for minimums). */
    onValidityChange?: (isValid: boolean) => void;
    /** Called when user taps an edit icon or bar in go-your-own-way mode. */
    onEditRequest?: (category: 'income' | 'bills' | 'spend' | 'goals') => void;
    /** Override the maximum width of the visual container. Defaults to 402. */
    maxWidth?: number;
}

interface Category {
    key: 'bills' | 'spend' | 'goals';
    label: string;
    filter: MoneyMapFilterState;
    cents: number;
    fillColor: string;
    lightColor: string;
    textColor: string;
    height: SharedValue<number>;
    activity: SharedValue<number>;
}

interface CurveProps {
    category: Category;
    startX: number;
    startY: number;
    endX: number;
    blocksBottom: number;
    endGap: number;
    strokeWidth: number;
    showWhenZero?: boolean;
    neutralColor?: string;
}

interface BarProps {
    category: Category;
    x: number;
    width: number;
    blocksBottom: number;
    showText: boolean;
    textPaddingTop?: number;
}

interface CategoryPressableProps {
    category: Category;
    x: number;
    width: number;
    blocksBottom: number;
    onPress: () => void;
}

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(max, value));

const getBarHeight = (
    cents: number,
    maxCents: number,
    minHeight: number,
    maxHeight: number,
): number => {
    if (cents <= 0) return minHeight;

    return clamp((cents / maxCents) * maxHeight, minHeight, maxHeight);
};

const useAnimatedNumber = (
    value: number,
    animated: boolean,
    initialValue: number,
): SharedValue<number> => {
    const sharedValue = useSharedValue(animated ? initialValue : value);

    useEffect(() => {
        sharedValue.value = animated
            ? withSpring(value, { damping: 15 })
            : value;
    }, [animated, sharedValue, value]);

    return sharedValue;
};

const useActivity = (active: boolean): SharedValue<number> => {
    const activity = useSharedValue(active ? 1 : 0);

    useEffect(() => {
        activity.value = withTiming(active ? 1 : 0, {
            duration: SELECTION_TRANSITION_MS,
        });
    }, [active, activity]);

    return activity;
};

const CategoryCurve = ({
    category,
    startX,
    startY,
    endX,
    blocksBottom,
    endGap,
    strokeWidth,
    showWhenZero = false,
    neutralColor,
}: CurveProps) => {
    const lightProps = useAnimatedProps(() => {
        const endY = blocksBottom - category.height.value - endGap;
        const curveHeight = Math.max(endY - startY, 0);
        const handle = curveHeight * CURVE_HANDLE_PERCENT;
        const hidden = category.cents <= 0 && !showWhenZero;

        return {
            d: `M ${startX} ${startY} C ${startX} ${startY + handle}, ${endX} ${endY - handle}, ${endX} ${endY}`,
            opacity: hidden ? 0 : 1,
        };
    });
    const activeProps = useAnimatedProps(() => {
        const endY = blocksBottom - category.height.value - endGap;
        const curveHeight = Math.max(endY - startY, 0);
        const handle = curveHeight * CURVE_HANDLE_PERCENT;
        const hidden = category.cents <= 0 && !showWhenZero;

        return {
            d: `M ${startX} ${startY} C ${startX} ${startY + handle}, ${endX} ${endY - handle}, ${endX} ${endY}`,
            opacity: hidden ? 0 : category.activity.value,
        };
    });

    return (
        <>
            <AnimatedPath
                animatedProps={lightProps}
                fill="none"
                stroke={neutralColor ?? category.lightColor}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {neutralColor == null && (
                <AnimatedPath
                    animatedProps={activeProps}
                    fill="none"
                    stroke={category.fillColor}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            )}
        </>
    );
};

const CategoryBar = ({
    category,
    x,
    width,
    blocksBottom,
    showText,
    textPaddingTop = 24,
}: BarProps) => {
    const geometryProps = useAnimatedProps(() => {
        const height = category.height.value;

        return {
            y: blocksBottom - height,
            height,
            rx: Math.min(16, height / 2, width / 2),
            opacity: category.cents <= 0 ? 0 : 1,
        };
    });
    const activeProps = useAnimatedProps(() => {
        const height = category.height.value;

        return {
            y: blocksBottom - height,
            height,
            rx: Math.min(16, height / 2, width / 2),
            opacity: category.cents <= 0 ? 0 : category.activity.value,
        };
    });
    const animatedAmountProps = useAnimatedProps(() => ({
        y: blocksBottom - category.height.value + textPaddingTop,
        opacity: 1,
    }));

    return (
        <>
            <AnimatedRect
                x={x}
                width={width}
                fill={category.lightColor}
                animatedProps={geometryProps}
            />
            <AnimatedRect
                x={x}
                width={width}
                fill={category.fillColor}
                animatedProps={activeProps}
            />
            {showText && (
                <>
                    <AnimatedText
                        x={x + width / 2}
                        fill={category.textColor}
                        fontFamily={REGULAR_FONT_FAMILY}
                        fontSize={16}
                        fontWeight={REGULAR_FONT_WEIGHT}
                        textAnchor="middle"
                        animatedProps={animatedAmountProps}
                        {...redactPrivate()}
                    >
                        {centsToFormattedDollars(category.cents, false)}
                    </AnimatedText>
                    <SvgText
                        x={x + width / 2}
                        y={blocksBottom - 12}
                        fill={category.textColor}
                        fontFamily={REGULAR_FONT_FAMILY}
                        fontSize={14}
                        fontWeight={REGULAR_FONT_WEIGHT}
                        textAnchor="middle"
                    >
                        {category.label}
                    </SvgText>
                </>
            )}
        </>
    );
};

interface CategoryBarEditIconProps {
    category: Category;
    barX: number;
    barWidth: number;
    blocksBottom: number;
    backgroundColor: string;
    iconColor: string;
    onPress?: () => void;
    alertMode?: boolean;
}

const CategoryBarEditIcon = ({
    category,
    barX,
    barWidth,
    blocksBottom,
    backgroundColor,
    iconColor,
    onPress,
    alertMode,
}: CategoryBarEditIconProps) => {
    const animatedStyle = useAnimatedStyle(() => ({
        top: blocksBottom - category.height.value,
        transform: [{ translateX: -16 }, { translateY: -16 }],
    }));

    return (
        <Animated.View
            style={[
                {
                    position: 'absolute',
                    left: barX + barWidth,
                    width: 32,
                    height: 32,
                    zIndex: 6,
                },
                animatedStyle,
            ]}
        >
            <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Edit ${category.label}`}
                onPress={onPress}
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                }}
            >
                <Icon
                    icon={alertMode ? TriangleAlertIcon : SquarePenIcon}
                    size={16}
                    stringColor={iconColor}
                />
            </Pressable>
        </Animated.View>
    );
};

const CategoryPressable = ({
    category,
    x,
    width,
    blocksBottom,
    onPress,
}: CategoryPressableProps) => {
    const animatedStyle = useAnimatedStyle(() => ({
        top: blocksBottom - category.height.value,
        height: category.height.value,
    }));

    return (
        <Animated.View
            style={[
                {
                    position: 'absolute',
                    left: x,
                    width,
                    zIndex: 5,
                },
                animatedStyle,
            ]}
        >
            <Pressable
                accessibilityRole="button"
                accessibilityLabel={`${category.label}, ${centsToFormattedDollars(category.cents, false)}`}
                onPress={onPress}
                style={{ flex: 1 }}
            />
        </Animated.View>
    );
};

const getMeasuredWidth = (event: LayoutChangeEvent, maxWidth: number): number =>
    Math.min(event.nativeEvent.layout.width, maxWidth);

export const MIN_GOALS_CENTS = 5000;
const MIN_SPEND_PERCENT = 0.1;

export const getMinSpendCents = (incomeCents: number) =>
    Math.round(incomeCents * MIN_SPEND_PERCENT);

export const isMoneyMapValid = (incomeCents: number, billsCents: number) => {
    const afterBills = incomeCents - billsCents;
    const minSpend = getMinSpendCents(incomeCents);
    return afterBills >= minSpend + MIN_GOALS_CENTS;
};

export const MoneyMapVisual = (props: MoneyMapVisualProps) => {
    if (props.variant === 'small') {
        return <SmallVariant {...props} />;
    }

    return <DefaultVariant {...props} />;
};

const DefaultVariant = ({
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    filter: filterProp,
    setFilter: setFilterProp,
    animated = true,
    mode = 'edit-mode',
    onValidityChange,
    onEditRequest,
    maxWidth: maxWidthProp,
}: MoneyMapVisualProps) => {
    const maxWidth = maxWidthProp ?? MAX_DEFAULT_WIDTH;
    const colors = useMoneyMapColors();
    const { getColor } = useThemeColors();
    const isEditActive = mode === 'edit-mode-active';
    const isGoYourOwnWay = mode === 'go-your-own-way';

    const valid = isMoneyMapValid(incomeTotalCents, billsTotalCents ?? 0);

    useEffect(() => {
        if (isGoYourOwnWay) {
            onValidityChange?.(valid);
        }
    }, [valid, isGoYourOwnWay, onValidityChange]);

    const neutralGray = getColor('$gray300');
    const [visualWidth, setVisualWidth] = useState(0);
    const filter = filterProp ?? MoneyMapFilterState.ALL;
    const isAllSelected = filter === MoneyMapFilterState.ALL;
    const isInteractive = setFilterProp != null;
    const billsCents = billsTotalCents ?? 0;
    const spendCents = spendTotalCents ?? 0;
    const goalsCents = goalsTotalCents ?? 0;
    const maxCents = Math.max(billsCents, spendCents, goalsCents, 1);
    const isActive = (categoryFilter: MoneyMapFilterState) =>
        isAllSelected || filter === categoryFilter;

    const billsHeight = getBarHeight(
        billsCents,
        maxCents,
        DEFAULT_LAYOUT.minBarHeight,
        DEFAULT_LAYOUT.maxBarHeight,
    );
    const spendHeight = getBarHeight(
        spendCents,
        maxCents,
        DEFAULT_LAYOUT.minBarHeight,
        DEFAULT_LAYOUT.maxBarHeight,
    );
    const goalsHeight = getBarHeight(
        goalsCents,
        maxCents,
        DEFAULT_LAYOUT.minBarHeight,
        DEFAULT_LAYOUT.maxBarHeight,
    );
    const animatedBillsHeight = useAnimatedNumber(
        billsHeight,
        animated,
        DEFAULT_LAYOUT.minBarHeight,
    );
    const animatedSpendHeight = useAnimatedNumber(
        spendHeight,
        animated,
        DEFAULT_LAYOUT.minBarHeight,
    );
    const animatedGoalsHeight = useAnimatedNumber(
        goalsHeight,
        animated,
        DEFAULT_LAYOUT.minBarHeight,
    );
    const billsActivity = useActivity(isActive(MoneyMapFilterState.BILLS));
    const spendActivity = useActivity(isActive(MoneyMapFilterState.SPEND));
    const goalsActivity = useActivity(isActive(MoneyMapFilterState.GOALS));
    const incomeActivity = useActivity(isAllSelected);

    const categories: Category[] = [
        {
            key: 'bills',
            label: 'Bills',
            filter: MoneyMapFilterState.BILLS,
            cents: billsCents,
            fillColor: colors.bills.fill,
            lightColor: colors.bills.light,
            textColor: colors.bills.text,
            height: animatedBillsHeight,
            activity: billsActivity,
        },
        {
            key: 'spend',
            label: 'Spend',
            filter: MoneyMapFilterState.SPEND,
            cents: spendCents,
            fillColor: colors.spend.fill,
            lightColor: colors.spend.light,
            textColor: colors.spend.text,
            height: animatedSpendHeight,
            activity: spendActivity,
        },
        {
            key: 'goals',
            label: 'Goals',
            filter: MoneyMapFilterState.GOALS,
            cents: goalsCents,
            fillColor: colors.goals.fill,
            lightColor: colors.goals.light,
            textColor: colors.goals.text,
            height: animatedGoalsHeight,
            activity: goalsActivity,
        },
    ];
    const centerX = visualWidth / 2;
    const barWidth =
        (visualWidth -
            DEFAULT_LAYOUT.horizontalPadding * 2 -
            DEFAULT_LAYOUT.barGap * 2) /
        3;
    const getBarX = (index: number) =>
        DEFAULT_LAYOUT.horizontalPadding +
        index * (barWidth + DEFAULT_LAYOUT.barGap);
    const curveStartY = DEFAULT_LAYOUT.curveStartY;
    const blocksBottom = DEFAULT_LAYOUT.blocksBottom;
    const visualHeight = DEFAULT_LAYOUT.height;
    const backgroundCategories = isAllSelected
        ? [...categories].reverse()
        : [...categories]
              .reverse()
              .filter((category) => category.filter !== filter);
    const inactiveCategories = isAllSelected
        ? categories
        : categories.filter((category) => category.filter !== filter);
    const selectedCategory = isAllSelected
        ? null
        : categories.find((category) => category.filter === filter);
    const incomeActiveProps = useAnimatedProps(() => ({
        opacity: incomeActivity.value,
    }));

    const toggleFilter = (categoryFilter: MoneyMapFilterState) => {
        if (setFilterProp == null) return;
        setFilterProp((currentFilter) =>
            currentFilter === categoryFilter
                ? MoneyMapFilterState.ALL
                : categoryFilter,
        );
    };

    return (
        <View
            style={{
                width: '100%',
                height: visualHeight,
                alignItems: 'center',
            }}
            onLayout={(event) => {
                setVisualWidth(getMeasuredWidth(event, maxWidth));
            }}
        >
            {visualWidth > 0 && (
                <View style={{ width: visualWidth, height: visualHeight }}>
                    <Svg width={visualWidth} height={visualHeight}>
                        <Rect
                            x={(visualWidth - DEFAULT_LAYOUT.pillWidth) / 2}
                            y={DEFAULT_LAYOUT.pillTop}
                            width={DEFAULT_LAYOUT.pillWidth}
                            height={DEFAULT_LAYOUT.pillHeight}
                            rx={16}
                            fill={colors.income.light}
                        />
                        <AnimatedRect
                            x={(visualWidth - DEFAULT_LAYOUT.pillWidth) / 2}
                            y={DEFAULT_LAYOUT.pillTop}
                            width={DEFAULT_LAYOUT.pillWidth}
                            height={DEFAULT_LAYOUT.pillHeight}
                            rx={16}
                            fill={colors.income.fill}
                            animatedProps={incomeActiveProps}
                        />
                        <SvgText
                            x={centerX}
                            y={DEFAULT_LAYOUT.pillTop + 22}
                            fill={colors.income.text}
                            fontFamily={REGULAR_FONT_FAMILY}
                            fontSize={14}
                            fontWeight={REGULAR_FONT_WEIGHT}
                            textAnchor="middle"
                        >
                            Take-home pay
                        </SvgText>
                        <SvgText
                            x={centerX}
                            y={DEFAULT_LAYOUT.pillTop + 51}
                            fill={colors.income.text}
                            fontFamily={REGULAR_FONT_FAMILY}
                            fontSize={16}
                            fontWeight={REGULAR_FONT_WEIGHT}
                            textAnchor="middle"
                            {...redactPrivate()}
                        >
                            {centsToFormattedDollars(incomeTotalCents, false)}
                        </SvgText>

                        <Path
                            d={`M ${centerX} ${DEFAULT_LAYOUT.connectorStartY} L ${centerX} ${DEFAULT_LAYOUT.connectorEndY}`}
                            fill="none"
                            stroke={
                                isEditActive ? neutralGray : colors.income.light
                            }
                            strokeWidth={DEFAULT_LAYOUT.strokeWidth}
                            strokeLinecap="round"
                        />
                        {!isEditActive && (
                            <AnimatedPath
                                d={`M ${centerX} ${DEFAULT_LAYOUT.connectorStartY} L ${centerX} ${DEFAULT_LAYOUT.connectorEndY}`}
                                fill="none"
                                stroke={colors.income.fill}
                                strokeWidth={DEFAULT_LAYOUT.strokeWidth}
                                strokeLinecap="round"
                                animatedProps={incomeActiveProps}
                            />
                        )}

                        {backgroundCategories.map((category) => {
                            const index = categories.findIndex(
                                (item) => item.key === category.key,
                            );

                            return (
                                <CategoryCurve
                                    key={`${category.key}-background-curve`}
                                    category={category}
                                    startX={centerX}
                                    startY={curveStartY}
                                    endX={getBarX(index) + barWidth / 2}
                                    blocksBottom={blocksBottom}
                                    endGap={DEFAULT_LAYOUT.curveToBarGap}
                                    strokeWidth={DEFAULT_LAYOUT.strokeWidth}
                                    showWhenZero
                                    neutralColor={
                                        isEditActive ? neutralGray : undefined
                                    }
                                />
                            );
                        })}

                        {inactiveCategories.map((category) => {
                            const index = categories.findIndex(
                                (item) => item.key === category.key,
                            );

                            return (
                                <CategoryBar
                                    key={`${category.key}-background-bar`}
                                    category={category}
                                    x={getBarX(index)}
                                    width={barWidth}
                                    blocksBottom={blocksBottom}
                                    showText
                                />
                            );
                        })}

                        {selectedCategory != null && (
                            <CategoryCurve
                                category={selectedCategory}
                                startX={centerX}
                                startY={curveStartY}
                                endX={
                                    getBarX(
                                        categories.findIndex(
                                            (item) =>
                                                item.key ===
                                                selectedCategory.key,
                                        ),
                                    ) +
                                    barWidth / 2
                                }
                                blocksBottom={blocksBottom}
                                endGap={DEFAULT_LAYOUT.curveToBarGap}
                                strokeWidth={DEFAULT_LAYOUT.strokeWidth}
                                showWhenZero
                                neutralColor={
                                    isEditActive ? neutralGray : undefined
                                }
                            />
                        )}

                        {selectedCategory != null && (
                            <CategoryBar
                                category={selectedCategory}
                                x={getBarX(
                                    categories.findIndex(
                                        (item) =>
                                            item.key === selectedCategory.key,
                                    ),
                                )}
                                width={barWidth}
                                blocksBottom={blocksBottom}
                                showText
                            />
                        )}
                    </Svg>

                    <View
                        style={{
                            position: 'absolute',
                            top: DEFAULT_LAYOUT.logoTop,
                            left: centerX - DEFAULT_LAYOUT.logoSize / 2,
                            width: DEFAULT_LAYOUT.logoSize,
                            height: DEFAULT_LAYOUT.logoSize,
                            borderRadius: 16,
                            backgroundColor: isEditActive
                                ? neutralGray
                                : colors.logoBg,
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 4,
                        }}
                    >
                        <FruitfulLogo color="#ffffff" width={32} height={32} />
                    </View>

                    {isEditActive && (
                        <Pressable
                            accessibilityRole="button"
                            accessibilityLabel="Edit take-home pay"
                            onPress={() => {
                                onEditRequest?.('income');
                            }}
                            style={{
                                position: 'absolute',
                                top: DEFAULT_LAYOUT.pillTop - 8,
                                left:
                                    centerX + DEFAULT_LAYOUT.pillWidth / 2 - 16,
                                width: 32,
                                height: 32,
                                borderRadius: 16,
                                backgroundColor: colors.income.lightest,
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 6,
                                cursor: 'pointer',
                            }}
                        >
                            <Icon
                                icon={SquarePenIcon}
                                size={16}
                                stringColor={colors.income.text}
                            />
                        </Pressable>
                    )}

                    {isGoYourOwnWay && (
                        <>
                            <Pressable
                                accessibilityRole="button"
                                accessibilityLabel={`Take-home pay, ${centsToFormattedDollars(incomeTotalCents, false)}`}
                                onPress={() => {
                                    onEditRequest?.('income');
                                }}
                                style={{
                                    position: 'absolute',
                                    top: DEFAULT_LAYOUT.pillTop,
                                    left:
                                        centerX - DEFAULT_LAYOUT.pillWidth / 2,
                                    width: DEFAULT_LAYOUT.pillWidth,
                                    height: DEFAULT_LAYOUT.pillHeight,
                                    zIndex: 5,
                                    cursor: 'pointer',
                                }}
                            />
                            <Pressable
                                accessibilityRole="button"
                                accessibilityLabel="Edit take-home pay"
                                onPress={() => {
                                    onEditRequest?.('income');
                                }}
                                style={{
                                    position: 'absolute',
                                    top: DEFAULT_LAYOUT.pillTop,
                                    left:
                                        centerX + DEFAULT_LAYOUT.pillWidth / 2,
                                    width: 32,
                                    height: 32,
                                    borderRadius: 16,
                                    backgroundColor: colors.income.lightest,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 6,
                                    cursor: 'pointer',
                                    transform: [
                                        { translateX: -16 },
                                        { translateY: -16 },
                                    ],
                                }}
                            >
                                <Icon
                                    icon={SquarePenIcon}
                                    size={16}
                                    stringColor={colors.income.darkest}
                                />
                            </Pressable>
                            {categories.map((category, index) => {
                                const spendGoalsLocked =
                                    !valid &&
                                    (category.key === 'spend' ||
                                        category.key === 'goals');
                                const onPressMap = {
                                    bills: () => {
                                        onEditRequest?.('bills');
                                    },
                                    spend: () => {
                                        if (!spendGoalsLocked)
                                            onEditRequest?.('spend');
                                    },
                                    goals: () => {
                                        if (!spendGoalsLocked)
                                            onEditRequest?.('goals');
                                    },
                                };
                                const colorMap = {
                                    bills: {
                                        bg: colors.bills.lightest,
                                        icon: colors.bills.dark,
                                    },
                                    spend: {
                                        bg: colors.spend.lightest,
                                        icon: colors.spend.dark,
                                    },
                                    goals: {
                                        bg: colors.goals.lightest,
                                        icon: colors.goals.dark,
                                    },
                                };
                                return (
                                    <CategoryBarEditIcon
                                        key={`${category.key}-edit`}
                                        category={category}
                                        barX={getBarX(index)}
                                        barWidth={barWidth}
                                        blocksBottom={blocksBottom}
                                        backgroundColor={
                                            colorMap[category.key].bg
                                        }
                                        iconColor={colorMap[category.key].icon}
                                        onPress={onPressMap[category.key]}
                                        alertMode={spendGoalsLocked}
                                    />
                                );
                            })}
                            {categories.map((category, index) => {
                                const spendGoalsLocked =
                                    !valid &&
                                    (category.key === 'spend' ||
                                        category.key === 'goals');
                                return (
                                    <CategoryPressable
                                        key={`${category.key}-pressable`}
                                        category={category}
                                        x={getBarX(index)}
                                        width={barWidth}
                                        blocksBottom={blocksBottom}
                                        onPress={() => {
                                            if (spendGoalsLocked) return;
                                            onEditRequest?.(category.key);
                                        }}
                                    />
                                );
                            })}
                        </>
                    )}

                    {isInteractive && !isEditActive && (
                        <>
                            <Pressable
                                accessibilityRole="button"
                                accessibilityLabel={`Take-home pay, ${centsToFormattedDollars(incomeTotalCents, false)}`}
                                onPress={() => {
                                    setFilterProp(MoneyMapFilterState.ALL);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: DEFAULT_LAYOUT.pillTop,
                                    left:
                                        centerX - DEFAULT_LAYOUT.pillWidth / 2,
                                    width: DEFAULT_LAYOUT.pillWidth,
                                    height: DEFAULT_LAYOUT.pillHeight,
                                    zIndex: 5,
                                }}
                            />
                            {categories.map((category, index) => (
                                <CategoryPressable
                                    key={`${category.key}-pressable`}
                                    category={category}
                                    x={getBarX(index)}
                                    width={barWidth}
                                    blocksBottom={blocksBottom}
                                    onPress={() => {
                                        toggleFilter(category.filter);
                                    }}
                                />
                            ))}
                        </>
                    )}
                </View>
            )}
        </View>
    );
};

const SmallVariant = ({
    incomeTotalCents,
    billsTotalCents,
    spendTotalCents,
    goalsTotalCents,
    filter: filterProp,
    setFilter: setFilterProp,
    animated = true,
}: MoneyMapVisualProps) => {
    const colors = useMoneyMapColors();
    const [visualWidth, setVisualWidth] = useState(0);
    const filter = filterProp ?? MoneyMapFilterState.ALL;
    const isAllSelected = filter === MoneyMapFilterState.ALL;
    const billsCents = billsTotalCents ?? 0;
    const spendCents = spendTotalCents ?? 0;
    const goalsCents = goalsTotalCents ?? 0;
    const maxCents = Math.max(billsCents, spendCents, goalsCents, 1);
    const isActive = (categoryFilter: MoneyMapFilterState) =>
        isAllSelected || filter === categoryFilter;

    const billsHeight = getBarHeight(
        billsCents,
        maxCents,
        SMALL_LAYOUT.minBarHeight,
        SMALL_LAYOUT.maxBarHeight,
    );
    const spendHeight = getBarHeight(
        spendCents,
        maxCents,
        SMALL_LAYOUT.minBarHeight,
        SMALL_LAYOUT.maxBarHeight,
    );
    const goalsHeight = getBarHeight(
        goalsCents,
        maxCents,
        SMALL_LAYOUT.minBarHeight,
        SMALL_LAYOUT.maxBarHeight,
    );
    const animatedBillsHeight = useAnimatedNumber(
        billsHeight,
        animated,
        SMALL_LAYOUT.minBarHeight,
    );
    const animatedSpendHeight = useAnimatedNumber(
        spendHeight,
        animated,
        SMALL_LAYOUT.minBarHeight,
    );
    const animatedGoalsHeight = useAnimatedNumber(
        goalsHeight,
        animated,
        SMALL_LAYOUT.minBarHeight,
    );
    const billsActivity = useActivity(isActive(MoneyMapFilterState.BILLS));
    const spendActivity = useActivity(isActive(MoneyMapFilterState.SPEND));
    const goalsActivity = useActivity(isActive(MoneyMapFilterState.GOALS));
    const incomeActivity = useActivity(isAllSelected);
    const incomeActiveStyle = useAnimatedStyle(() => ({
        opacity: incomeActivity.value,
    }));
    const categories: Category[] = [
        {
            key: 'bills',
            label: 'Bills',
            filter: MoneyMapFilterState.BILLS,
            cents: billsCents,
            fillColor: colors.bills.fill,
            lightColor: colors.bills.light,
            textColor: colors.bills.text,
            height: animatedBillsHeight,
            activity: billsActivity,
        },
        {
            key: 'spend',
            label: 'Spend',
            filter: MoneyMapFilterState.SPEND,
            cents: spendCents,
            fillColor: colors.spend.fill,
            lightColor: colors.spend.light,
            textColor: colors.spend.text,
            height: animatedSpendHeight,
            activity: spendActivity,
        },
        {
            key: 'goals',
            label: 'Goals',
            filter: MoneyMapFilterState.GOALS,
            cents: goalsCents,
            fillColor: colors.goals.fill,
            lightColor: colors.goals.light,
            textColor: colors.goals.text,
            height: animatedGoalsHeight,
            activity: goalsActivity,
        },
    ];
    const barWidth =
        (visualWidth - SMALL_LAYOUT.barGap * 2) / categories.length;
    const getBarX = (index: number) => index * (barWidth + SMALL_LAYOUT.barGap);
    const centerX = visualWidth / 2;
    const backgroundCategories = isAllSelected
        ? [...categories].reverse()
        : [...categories]
              .reverse()
              .filter((category) => category.filter !== filter);
    const inactiveCategories = isAllSelected
        ? categories
        : categories.filter((category) => category.filter !== filter);
    const selectedCategory = isAllSelected
        ? null
        : categories.find((category) => category.filter === filter);

    const toggleFilter = (categoryFilter: MoneyMapFilterState) => {
        if (setFilterProp == null) return;
        setFilterProp((currentFilter) =>
            currentFilter === categoryFilter
                ? MoneyMapFilterState.ALL
                : categoryFilter,
        );
    };

    return (
        <View
            style={{
                width: '100%',
                height: SMALL_LAYOUT.height,
                alignItems: 'center',
            }}
            onLayout={(event) => {
                setVisualWidth(getMeasuredWidth(event, MAX_SMALL_WIDTH));
            }}
        >
            {visualWidth > 0 && (
                <View
                    style={{
                        width: visualWidth,
                        height: SMALL_LAYOUT.height,
                    }}
                >
                    <View
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: centerX - SMALL_LAYOUT.pillWidth / 2,
                            width: SMALL_LAYOUT.pillWidth,
                            height: SMALL_LAYOUT.pillHeight,
                            borderRadius: 16,
                            backgroundColor: colors.income.light,
                            paddingVertical: 8,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            overflow: 'hidden',
                        }}
                    >
                        <Animated.View
                            pointerEvents="none"
                            style={[
                                {
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                    backgroundColor: colors.income.fill,
                                },
                                incomeActiveStyle,
                            ]}
                        />
                        <BodySmallRegular
                            color={colors.income.text}
                            position="relative"
                            zIndex={1}
                        >
                            Take-home pay
                        </BodySmallRegular>
                        <BodyMediumRegular
                            color={colors.income.text}
                            position="relative"
                            zIndex={1}
                            {...redactPrivate()}
                        >
                            {centsToFormattedDollars(incomeTotalCents, false)}
                        </BodyMediumRegular>
                    </View>

                    <Svg width={visualWidth} height={SMALL_LAYOUT.height}>
                        {backgroundCategories.map((category) => {
                            const index = categories.findIndex(
                                (item) => item.key === category.key,
                            );

                            return (
                                <CategoryCurve
                                    key={`${category.key}-background-curve`}
                                    category={category}
                                    startX={centerX}
                                    startY={SMALL_LAYOUT.curveStartY}
                                    endX={getBarX(index) + barWidth / 2}
                                    blocksBottom={SMALL_LAYOUT.blocksBottom}
                                    endGap={SMALL_LAYOUT.curveToBarGap}
                                    strokeWidth={SMALL_LAYOUT.strokeWidth}
                                    showWhenZero
                                />
                            );
                        })}

                        {inactiveCategories.map((category) => {
                            const index = categories.findIndex(
                                (item) => item.key === category.key,
                            );

                            return (
                                <CategoryBar
                                    key={`${category.key}-background-bar`}
                                    category={category}
                                    x={getBarX(index)}
                                    width={barWidth}
                                    blocksBottom={SMALL_LAYOUT.blocksBottom}
                                    showText={false}
                                />
                            );
                        })}

                        {selectedCategory != null && (
                            <CategoryCurve
                                category={selectedCategory}
                                startX={centerX}
                                startY={SMALL_LAYOUT.curveStartY}
                                endX={
                                    getBarX(
                                        categories.findIndex(
                                            (item) =>
                                                item.key ===
                                                selectedCategory.key,
                                        ),
                                    ) +
                                    barWidth / 2
                                }
                                blocksBottom={SMALL_LAYOUT.blocksBottom}
                                endGap={SMALL_LAYOUT.curveToBarGap}
                                strokeWidth={SMALL_LAYOUT.strokeWidth}
                                showWhenZero
                            />
                        )}

                        {selectedCategory != null && (
                            <CategoryBar
                                category={selectedCategory}
                                x={getBarX(
                                    categories.findIndex(
                                        (item) =>
                                            item.key === selectedCategory.key,
                                    ),
                                )}
                                width={barWidth}
                                blocksBottom={SMALL_LAYOUT.blocksBottom}
                                showText={false}
                            />
                        )}
                    </Svg>

                    <View
                        style={{
                            position: 'absolute',
                            top: SMALL_LAYOUT.labelsTop,
                            left: 0,
                            width: visualWidth,
                            flexDirection: 'row',
                            gap: SMALL_LAYOUT.barGap,
                        }}
                    >
                        {categories.map((category) => (
                            <Pressable
                                key={`${category.key}-label`}
                                accessibilityRole={
                                    setFilterProp == null ? undefined : 'button'
                                }
                                accessibilityLabel={`${category.label}, ${centsToFormattedDollars(category.cents, false)}`}
                                disabled={setFilterProp == null}
                                onPress={() => {
                                    toggleFilter(category.filter);
                                }}
                                style={{
                                    width: barWidth,
                                    alignItems: 'center',
                                }}
                            >
                                <BodySmallRegular
                                    color="$black"
                                    lineHeight={16}
                                >
                                    {category.label}
                                </BodySmallRegular>
                                <BodyMediumEmphasized
                                    color="$black"
                                    {...redactPrivate()}
                                >
                                    {centsToFormattedDollars(
                                        category.cents,
                                        false,
                                    )}
                                </BodyMediumEmphasized>
                            </Pressable>
                        ))}
                    </View>

                    {setFilterProp != null &&
                        categories.map((category, index) => (
                            <CategoryPressable
                                key={`${category.key}-pressable`}
                                category={category}
                                x={getBarX(index)}
                                width={barWidth}
                                blocksBottom={SMALL_LAYOUT.blocksBottom}
                                onPress={() => {
                                    toggleFilter(category.filter);
                                }}
                            />
                        ))}
                </View>
            )}
        </View>
    );
};
